﻿import IClonable from "../types/IClonable";
import IEquatable from "../types/IEquatable";
import {DateTime} from "./DateTime";
import {createFromNumbers, dateCompare, dateEquals, fromUtc, getLastOfMonth, partsInTimeZone, throwsIfInvalidDate} from "./DateTimeHelpers";
import {DayOfWeek} from "./DayOfWeek";
import {TimeSpan} from "./TimeSpan";

function toISOString(date: Date): string {
	return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds())).toISOString().slice(0, 10);
}

export class DateOnly implements IEquatable<DateOnly>, IClonable<DateOnly> {
	private static _maxValue: DateOnly | null = null;
	private static _minValue: DateOnly | null = null;
	private readonly _date: Date;

	private constructor(date: Date, isUtc: boolean = false) {
		this._date = isUtc ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0) : new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0);
		throwsIfInvalidDate(this._date, "DateOnly");
	}

	get [Symbol.toStringTag](): string {
		return "DateOnly";
	}

	[Symbol.toPrimitive](hint: string): number | string {
		if (hint === "number") {
			return fromUtc(this._date).getTime();
		}

		return toISOString(this._date);
	}

	/**
	 * Adds the specified number of days to the value of this instance.
	 * @param days The number of days to add. Can be negative to subtract days.
	 * @returns A new DateOnly that adds the specified number of days to the value of this instance.
	 */
	addDays(days: number): DateOnly {
		const newDate = new Date(this._date);
		newDate.setDate(newDate.getDate() + days);
		return new DateOnly(newDate, true);
	}

	/**
	 * Adds the specified number of months to the value of this instance.
	 * @param months The number of months to add. Can be negative to subtract months.
	 * @returns A new DateOnly that adds the specified number of months to the value of this instance.
	 */
	addMonths(months: number): DateOnly {
		const newDate = new Date(this._date);
		newDate.setMonth(newDate.getMonth() + months);
		return new DateOnly(newDate, true);
	}

	/**
	 * Adds the specified number of weeks to the value of this instance.
	 * @param weeks The number of weeks to add. Can be negative to subtract weeks.
	 * @returns A new DateOnly that adds the specified number of weeks to the value of this instance.
	 */
	addWeeks(weeks: number): DateOnly {
		return this.addDays(weeks * 7);
	}

	/**
	 * Adds the specified number of years to the value of this instance.
	 * @param years The number of years to add. Can be negative to subtract years.
	 * @returns A new DateOnly that adds the specified number of years to the value of this instance.
	 */
	addYears(years: number): DateOnly {
		const newDate = new Date(this._date);
		newDate.setFullYear(newDate.getFullYear() + years);
		return new DateOnly(newDate, true);
	}

	/**
	 * Creates a new object that is a copy of the current instance.
	 * @returns A new DateOnly object that is a copy of this instance.
	 */
	clone(): DateOnly {
		return new DateOnly(this._date, true);
	}

	/**
	 * Compares the value of this instance to a specified DateTime, DateOnly object, null or undefined.
	 * @param other The object to compare to the current instance.
	 * @returns A signed number indicating the relative values:
	 * - Less than 0 if this instance is earlier than the other
	 * - 0 if this instance is equal to the other
	 * - Greater than 0 if this instance is later than the other
	 * - 1 if other is null or undefined
	 */
	compareTo(other: DateOnly | DateTime | null | undefined): number {
		return dateCompare(this._date, other instanceof DateTime ? other.getUtcDate() : other?._date);
	}

	/**
	 * Returns the TimeSpan value represented by the difference between the value of this instance and the specified DateOnly object.
	 * @param value The DateOnly value to subtract.
	 * @returns A TimeSpan value that represents the duration between the value of the current instance and the specified DateOnly object.
	 */
	diff(value: DateOnly): TimeSpan {
		return TimeSpan.fromTicks(this.getTicks() - value.getTicks());
	}

	/**
	 * Determines whether the specified object is equal to the current instance.
	 * @param other The object to compare to this instance.
	 * @returns true if the specified object is equal to the current instance; otherwise, false.
	 */
	equals(other: DateOnly | DateTime | null | undefined): boolean {
		if (other instanceof DateTime) {
			return other.toDateOnly().equals(this);
		}

		return dateEquals(this._date, other?._date);
	}

	/**
	 * Returns an integer, between 1 and 31, representing the day of the month for the given date according to local time.
	 * @return {number} The day of the month as a number.
	 */
	getDay(): number {
		return this._date.getDate();
	}

	/**
	 * Returns the day of the week for this date.
	 * @returns A DayOfWeek value representing the day of the week (0 = Sunday, 6 = Saturday).
	 */
	getDayOfWeek(): DayOfWeek {
		return this._date.getDay();
	}

	/**
	 * Returns the last day of the month for this date.
	 * @returns The last day of the month as a number (28-31).
	 */
	getLastOfMonth(): number {
		return getLastOfMonth(this._date);
	}

	/**
	 * Returns an integer, between 1 and 12, representing the month for the given date according to local time.
	 */
	getMonth(): number {
		return this._date.getMonth() + 1;
	}

	/**
	 * Returns the number of ticks that represent the date and time of this instance.
	 * @returns The number of ticks that represent the date and time of this instance.
	 */
	getTicks(): number {
		return fromUtc(this._date).getTime();
	}

	/**
	 * Returns the underlying Date object in UTC.
	 * @returns The underlying Date object.
	 */
	getUtcDate(): Date {
		return this._date;
	}

	/**
	 * Returns the year component of the date.
	 * @returns The year as a four-digit number.
	 */
	getYear(): number {
		return this._date.getFullYear();
	}

	/**
	 * Converts the value of the current DateOnly object to its equivalent JSON string representation.
	 * @returns A string in ISO 8601 format (YYYY-MM-DD).
	 */
	toJSON(): string {
		return toISOString(this._date);
	}

	/**
	 * Converts this date (midnight, treated as UTC) to the equivalent calendar date in the specified
	 * IANA time zone. The day may shift forward or back depending on the zone offset. No time zone is
	 * stored on the returned object.
	 * @param timeZone An IANA time zone name, e.g. any value from Intl.supportedValuesOf("timeZone").
	 * @returns A new DateOnly for this instant's calendar date in the given time zone.
	 * @throws RangeError if timeZone is not a valid IANA time zone name.
	 */
	toTimeZone(timeZone: string): DateOnly {
		const p = partsInTimeZone(this.getTicks(), timeZone);
		return DateOnly.fromComponents(p.year, p.month, p.day);
	}

	/**
	 * Converts the value of the current DateOnly object to its equivalent string representation.
	 * @returns A string in ISO 8601 format (YYYY-MM-DD).
	 */
	toString(): string {
		return toISOString(this._date);
	}

	/**
	 * Creates a DateOnly from year, month, and day values
	 * @param year The full year
	 * @param month The month (1-12)
	 * @param day The day of month (1-31)
	 */
	static fromComponents(year: number, month: number = 1, day: number = 1): DateOnly {
		return new DateOnly(createFromNumbers(year, month, day));
	}

	/**
	 * Creates a DateOnly from a Date object.
	 * @param date The Date object to convert.
	 * @param isUtc If true, takes the Date's local-field components as the calendar date (use when
	 * those fields already hold the intended date, e.g. DateTime's internal UTC wall-clock). If false
	 * (default), takes the calendar date of the instant in UTC.
	 * @returns A new DateOnly instance.
	 */
	static fromDate(date: Date, isUtc: boolean = false): DateOnly {
		return new DateOnly(date, isUtc);
	}

	/**
	 * Creates a DateOnly from a date string in ISO 8601 format.
	 * @param dateString A date string in ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss).
	 * @returns A new DateOnly instance.
	 */
	static fromIsoString(dateString: string): DateOnly {
		if (dateString.includes("T")) {
			dateString = dateString.split("T")[0];
		}

		return new DateOnly(new Date(dateString + "T00:00:00Z"));
	}

	/**
	 * Creates a DateOnly from a timestamp.
	 * @param timestamp The number of milliseconds since the Unix epoch (January 1, 1970, 00:00:00 UTC).
	 * @returns A new DateOnly instance.
	 */
	static fromTimestamp(timestamp: number): DateOnly {
		return new DateOnly(new Date(timestamp));
	}

	/**
	 * Represents the largest possible value of DateOnly.
	 * @returns The largest possible DateOnly value.
	 */
	static maxValue(): DateOnly {
		//9999-12-31 (.NET DateOnly.MaxValue): the ECMAScript max instant re-read as a local
		//date exceeds the Date range in any west-of-UTC timezone and threw Invalid Date
		return this._maxValue ??= DateOnly.fromComponents(9999, 12, 31);
	}

	/**
	 * Represents the smallest possible value of DateOnly.
	 * @returns The smallest possible DateOnly value (Unix epoch: January 1, 1970).
	 */
	static minValue(): DateOnly {
		return this._minValue ??= DateOnly.fromTimestamp(0);
	}

	/**
	 * Creates a DateOnly representing the current date in local time.
	 * @returns A new DateOnly instance representing today's date.
	 */
	static today(): DateOnly {
		//isUtc=true reads the Date's local fields — the user's calendar date, not the UTC one
		return new DateOnly(new Date(), true);
	}
}
